import { z } from "zod";

/**
 * Common reusable Zod schemas for API operations across the server package.
 * Import these schemas instead of redeclaring them in individual schema files.
 *
 * Usage:
 * import { objectIdString, filterWhereClause, sortConfig, statusResponse } from "../../helpers/schemas/common.schema.js";
 */

// ============================================================================
// MongoDB ObjectId Schema
// ============================================================================

/**
 * MongoDB ObjectId as a 24-character hex string with validation.
 */
export const objectIdString = z
  .string()
  .trim()
  .regex(/^[a-fA-F0-9]{24}$/, "Invalid ObjectId format")
  .meta({
    description: "MongoDB ObjectId as 24-character hex string",
    example: "64b2c123df890abc1234abcd",
  })
  .brand<"ObjectId">();

/**
 * Tag an id that is ALREADY known to be a valid ObjectId as an {@link ObjectIdString}.
 *
 * Use this for values that came OUT of MongoDB (a `doc._id`, an `insertedId`) or that the
 * server generated itself — they have already passed the trust boundary, so re-running the
 * hex regex on every document→DTO mapping would be pure overhead and would add a throw path
 * where a failure is impossible.
 *
 * For anything client-supplied use `objectIdString.parse()` / `.safeParse()` instead — that
 * is the boundary the brand exists to protect. Accepts `ObjectId` (structurally, so helpers
 * needs no mongodb dependency) or a string.
 */
export function toObjectIdString(id: { toString(): string } | string): ObjectIdString;
export function toObjectIdString(
  id: { toString(): string } | string | null | undefined,
): ObjectIdString | undefined;
export function toObjectIdString(
  id: { toString(): string } | string | null | undefined,
): ObjectIdString | undefined {
  // Mirrors `x?._id?.toString()` semantics so nullish chains keep propagating undefined
  // instead of stringifying into the literal "undefined".
  return id == null ? undefined : (String(id) as ObjectIdString);
}

// ============================================================================
// Email Schema
// ============================================================================

/**
 * Email address with trim-then-validate semantics.
 *
 * In Zod v4, `.trim()` chained after `z.email()` runs AFTER validation, so
 * leading/trailing whitespace causes the email check to fail. This schema
 * trims first via `.pipe()` so " user@example.com " is accepted.
 *
 * Max length is 254 chars (RFC 5321). Callers add `.optional()` / `.nullable()`
 * as needed.
 */
export const emailString = z
  .string()
  .trim()
  .pipe(z.email().max(254))
  .meta({
    description: "Email address (trimmed, RFC-validated, max 254 chars per RFC 5321)",
    example: "user@example.com",
  });

// ============================================================================
// AWS / Temp Upload Key Schema
// ============================================================================

/**
 * Key returned by `/fileSystemServer/uploadFileInTemp`. Shape is `<uuid-v4>.<ext>`
 * (see `packages/server/fileUpload/fileUpload.service.ts` – `file.__uuid__`).
 * Use this anywhere a request accepts an awsKey freshly produced by the temp
 * upload endpoint, so path-traversal / arbitrary strings are rejected before
 * they reach `transferFileFromTempToMainStorage`.
 */
export const tempUploadKey = z
  .string()
  .trim()
  .min(1)
  .max(256)
  .regex(
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\.[A-Za-z0-9]+$/i,
    "awsKey must be a uuid.<extension> produced by /fileSystemServer/uploadFileInTemp",
  )
  .meta({
    description: "Temp-upload key (uuid.<extension>) returned by /fileSystemServer/uploadFileInTemp",
    example: "f47ac10b-58cc-4372-a567-0e02b2c3d479.mp4",
  });

// ============================================================================
// Grid/Query Filter Schemas
// ============================================================================

/**
 * Filter where clause for grid queries.
 * Accepts both record format (object) and array format (Syncfusion DataManager).
 */
export const filterWhereClause = z
  // record (object) OR array (Syncfusion DataManager) — same inputs the previous
  // z.custom accepted, but JSON-Schema-representable so it can be advertised to API/MCP clients.
  .union([z.record(z.string(), z.unknown()), z.array(z.unknown())])
  .optional()
  .meta({
    description: "Filter conditions for grid queries (supports both record and array formats)",
  });

/**
 * Build a filter `where` schema locked to an explicit allow-list of filterable fields.
 *
 * Why this exists: `buildFilterWhereClause` (server/helpers/queryBuilder.ts) drops each
 * condition's `field` straight into a MongoDB query key (`{ [field]: ... }`). With the
 * open {@link filterWhereClause} a client can therefore filter on — and probe the value
 * of — ANY field in a document, including embedded/internal fields that are never shown
 * in the grid (e.g. `portalId`, `users.modulePermissions.*`). This factory restricts the
 * accepted `field` values to the columns a grid genuinely exposes for filtering, rejecting
 * anything else before it reaches Mongo.
 *
 * It still accepts every payload shape the Syncfusion/SlickGrid filter UI sends:
 *   - a flat predicate: `{ field, operator, value, isComplex: false, ... }`
 *   - a grouped predicate: `{ isComplex: true, condition: "and"|"or", predicates: [...] }`
 *     (validated recursively — every leaf `field` must be allowed)
 *   - the global-search form: `{ field: "a,b,c", operator: "globalsearch", value }`
 *
 * `operator` is kept as a free string (not the {@link filterOperator} enum) on purpose:
 * the UI emits operators outside that enum (`between`, `empty`, `null`, …) and the field
 * allow-list — not the operator — is the security control.
 *
 * `value` is constrained to scalars / arrays-of-scalars so an attacker cannot smuggle a
 * raw Mongo operator object (e.g. `{ $ne: null }`, `{ $gt: "" }`) through to the query
 * builder, which would otherwise drop it verbatim into the query.
 *
 * `globalsearch` (a UI search box that substring-matches across a comma-separated, UI-derived
 * column list) is not rejected — it is NARROWED: any field in its list that is not on the
 * allow-list is dropped, so the search can never reach internal fields. Legitimate searches
 * (whose fields are all allow-listed) are unaffected; if every field is dropped the empty
 * list yields a harmless no-op clause server-side.
 */
/**
 * Reusable allow-patterns for grids that expose DYNAMIC, per-project filterable columns
 * whose field names cannot be enumerated statically. Pass via the `patterns` option of
 * {@link filterWhereClauseFor}.
 *
 * - `customFieldFilterPattern` — form-template custom-field columns: `customFields.<id>`
 *   and `projectLevelCustomFields.<id>`, where <id> is either a 24-hex ObjectId OR a 36-char
 *   UUID v4 (built by SlickGrid1/customFields.tsx `getFieldKey`; e.g. transmittal form-question
 *   columns use the question's UUID `_id`).
 * - `formQuestionFilterPattern` — form-question filters, keyed by the bare `questionId` (a
 *   36-char UUID v4, client-generated — see forms.schema.ts questionFieldSchema._id) consumed
 *   by `buildFilterWhereClause(..., formsPatch=true)` (server/helpers/queryBuilder.ts).
 */
export const customFieldFilterPattern =
  /^(customFields|projectLevelCustomFields)\.([a-fA-F0-9]{24}|[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})$/;
export const formQuestionFilterPattern = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

/**
 * Both dynamic-column patterns together. Safe to pass to ANY grid endpoint: these patterns
 * only match user-defined custom-field / form-question columns (`customFields.<id>`,
 * `<uuid>`) — never internal system fields — so they never reopen the arbitrary-field
 * hole, and including them prevents 400s on grids that render dynamic custom-field columns.
 */
export const dynamicCustomFieldPatterns: readonly RegExp[] = [customFieldFilterPattern, formQuestionFilterPattern];

/**
 * A single filter `value` exactly as {@link filterWhereClauseFor}'s internal `filterValue` constrains it
 * AT RUNTIME: scalars and arrays-of-scalars ONLY. That restriction is itself the security control — it is
 * what stops a client smuggling a raw Mongo operator object (`{ $ne: null }`, `{ $gt: "" }`) into a query
 * key, since the query builder drops values in verbatim. Do NOT widen the schema.
 */
type FilterConditionScalar = string | number | boolean | null;
export type FilterConditionValue = FilterConditionScalar | FilterConditionScalar[];

/**
 * One leaf/group condition of a `where` payload validated by {@link filterWhereClauseFor}, and the element
 * type its `z.infer` now yields. It used to collapse to `unknown`, because the recursive `condition` below
 * was annotated as a bare `z.ZodType` — which is `ZodType<unknown, unknown>` — so every validated `where`
 * element erased, and `server/helpers/queryBuilder.ts` had to take `readonly unknown[]`.
 *
 * Every member is optional because the schema accepts all three Syncfusion/SlickGrid payload shapes (flat
 * predicate, grouped predicate, global search), so nothing may be assumed present. `field` stays a plain
 * `string` and `operator` a FREE `string` deliberately — see the factory's doc comment: the field
 * ALLOW-LIST, enforced at runtime by `superRefine` (not by this type), is the security control, and the
 * grid UI legitimately emits operators outside the {@link filterOperator} enum (`between`, `empty`, …).
 *
 * `value` is declared `unknown` rather than {@link FilterConditionValue}. That is a deliberate, sound
 * WIDENING of the real output — the Zod `filterValue` union still rejects objects at runtime, so the
 * `{ $ne: null }` control is fully intact — and it is required for cross-package assignability: the web
 * grid's own `PredicateShape` (web/src/services/queryBuilder.ts) declares `value: unknown`, so a narrower
 * declaration here would stop `packages/web` compiling. For the same reason there is NO `[key: string]:
 * unknown` index signature mirroring `looseObject`: TypeScript grants no implicit index signature to
 * interfaces, so adding one rejects every interface-typed caller (`PredicateShape` included) while
 * `Record<string, unknown>` callers are accepted without it.
 */
export interface FilterWhereCondition {
  field?: string;
  operator?: string;
  value?: unknown;
  isComplex?: boolean;
  condition?: string;
  predicates?: FilterWhereCondition[];
  /**
   * Syncfusion/SlickGrid case+accent flags. `looseObject` passes them through at runtime, and both are
   * already declared by {@link whereClauseSchema}'s predicate and by `queryBuilder.ts`'s FilterCondition.
   * They MUST be named here rather than left to an index signature: several `packages/web` actions wrap
   * their payload in type-fest's `Exact<>`, which turns any key absent from this interface into `never`.
   */
  ignoreCase?: boolean;
  ignoreAccent?: boolean;
}

/**
 * @param allowedFields exact field names a client may filter on.
 * @param opts.patterns optional RegExps; a field is also allowed if it matches any pattern
 *   (use for grids with dynamic custom-field / form-question columns — see the pattern consts).
 */
export function filterWhereClauseFor(
  allowedFields: readonly string[],
  opts: { patterns?: readonly RegExp[] } = {},
) {
  const allowed = new Set(allowedFields);
  const patterns = opts.patterns ?? [];
  const isAllowed = (field: string) => allowed.has(field) || patterns.some((re) => re.test(field));
  const allowedList = allowedFields.join(", ") + (patterns.length ? " (plus dynamic custom-field columns)" : "");

  // Scalars and arrays-of-scalars only — never a raw object (blocks `{ $ne: ... }` etc.).
  const filterScalar = z.union([z.string(), z.number(), z.boolean(), z.null()]);
  const filterValue = z.union([filterScalar, z.array(filterScalar)]).optional();

  // Narrow a global-search field list to allow-listed columns only. Runs as a
  // `z.preprocess` (input coercion) rather than a trailing `.transform()` ON PURPOSE:
  // `.transform()` makes `z.toJSONSchema` throw ("Transforms cannot be represented"),
  // which would force every endpoint using this filter to fall back to an opaque
  // `payload` shape when advertised to API/MCP clients. `z.preprocess(fn, inner)` does
  // the same narrowing but stays JSON-Schema-representable. Do NOT switch this back to
  // `.transform()`.
  const narrowGlobalSearch = (raw: unknown) => {
    const cond = raw as { operator?: string; field?: unknown } | null;
    if (cond && cond.operator === "globalsearch" && typeof cond.field === "string") {
      const narrowed = cond.field
        .split(",")
        .map((f) => f.trim())
        .filter((f) => isAllowed(f));
      return { ...cond, field: narrowed.join(",") };
    }
    return raw;
  };

  // Recursive schemas need an explicit annotation (TypeScript cannot infer a self-referential type), and
  // that annotation is exactly what `z.infer` reports. Naming the real node type here — instead of the
  // bare `z.ZodType`, i.e. `ZodType<unknown, unknown>`, this used to carry — is what lets
  // `FilterWhereCondition` flow out to every routed grid endpoint. The INPUT side is left at `ZodType`'s
  // `unknown` default on purpose: `z.preprocess` genuinely accepts unknown input, so nothing that used to
  // parse stops parsing and no caller's request-payload type is narrowed.
  const condition: z.ZodType<FilterWhereCondition> = z.lazy(() =>
    z.preprocess(
      narrowGlobalSearch,
      z
        .looseObject({
          field: z.string().optional(),
          operator: z.string().optional(),
          value: filterValue,
          isComplex: z.boolean().optional(),
          condition: z.string().optional(),
          predicates: z.array(condition).optional(),
        })
        .superRefine((cond, ctx) => {
          // Grouped conditions carry no `field`; their leaves are validated recursively
          // via `predicates`. Global search is narrowed (not rejected) by the preprocess
          // above, so it does not need an allow-list check here.
          if (cond.operator === "globalsearch") return;
          if (typeof cond.field !== "string" || cond.field.length === 0) return;
          if (!isAllowed(cond.field)) {
            ctx.addIssue({
              code: "custom",
              path: ["field"],
              message: `Filtering on "${cond.field}" is not allowed. Filterable fields: ${allowedList}.`,
            });
          }
        }),
    ),
  );

  return z
    .array(condition)
    .optional()
    .meta({
      description:
        "Filter for list/grid queries — an ARRAY of conditions ANDed together, e.g. " +
        `[{ field: "${allowedFields[0] ?? "name"}", operator: "contains", value: "Tower-A" }]. ` +
        `Only these fields may be filtered: ${allowedList}.`,
    });
}

/**
 * Sort configuration for grid queries.
 * Specifies the field to sort by and direction.
 */
export const sortConfig = z
  .object({
    // Non-empty: an empty sort field resolves to `{ "": -1 }`, which Mongo rejects with
    // "FieldPath cannot be constructed with empty string" — reject it at the boundary instead.
    name: z.string().min(1).meta({
      description: "Field name to sort by",
    }),
    direction: z.enum(["ascending", "descending"]).optional().meta({
      description: "Sort direction, defaults to ascending if not specified",
    }),
  })
  .meta({
    description: "Sort configuration for grid queries",
  });

/**
 * Array of sort configurations for multi-column sorting.
 */
export const sortConfigArray = z.array(sortConfig).optional().meta({
  description: "Array of sort configurations for multi-column sorting",
});

/**
 * Build a `sorted` schema locked to an explicit allow-list of sortable columns. Companion to
 * {@link filterWhereClauseFor}. `buildFilterSortClause` / `buildFilterSortMongoClause`
 * (server/helpers/queryBuilder.ts) drop `sortClause[0].name` straight into a Mongo sort key
 * (`{ [name]: 1 | -1 }`), so the open {@link sortConfig} lets a client sort on — and, via ordering +
 * pagination, order-probe — ANY field, and force an unindexed blocking `$sort` (DoS). This restricts
 * the accepted `name` to the columns a grid actually exposes and REJECTS anything else at the Zod
 * boundary (400), matching the per-column reject behaviour of filterWhereClauseFor's `field`.
 *
 * Drop-in for `sortConfigArray` / `z.array(sortConfig).optional()` / `z.array(z.any()).optional()`.
 * `opts.patterns` (pass {@link dynamicCustomFieldPatterns}) additionally allows dynamic custom-field /
 * form-question sort columns. NOTE: the query builder applies only `sortClause[0]`, but every entry is
 * still allow-list checked, so a disallowed column at index 1+ still 400s (fail-closed).
 */
export function sortConfigFor(allowedFields: readonly string[], opts: { patterns?: readonly RegExp[] } = {}) {
  const allowed = new Set(allowedFields);
  const patterns = opts.patterns ?? [];
  const isAllowed = (name: string) => allowed.has(name) || patterns.some((re) => re.test(name));
  const allowedList = allowedFields.join(", ") + (patterns.length ? " (plus dynamic custom-field columns)" : "");

  const entry = z
    .object({
      // Non-empty (an empty sort field resolves to `{ "": -1 }`, which Mongo rejects) AND allow-listed.
      name: z.string().min(1).meta({ description: `Column to sort by. Sortable columns: ${allowedList}.` }),
      direction: z.enum(["ascending", "descending"]).optional().meta({
        description: "Sort direction, defaults to ascending if not specified",
      }),
    })
    // superRefine (NOT .transform) keeps the schema z.toJSONSchema-representable, like filterWhereClauseFor.
    .superRefine((cfg, ctx) => {
      if (!isAllowed(cfg.name)) {
        ctx.addIssue({
          code: "custom",
          path: ["name"],
          message: `Sorting on "${cfg.name}" is not allowed. Sortable columns: ${allowedList}.`,
        });
      }
    });

  return z
    .array(entry)
    .optional()
    .meta({
      description:
        "Sort configuration for list/grid queries — an array of { name, direction? }; only the first entry is " +
        `applied server-side. Only these columns may be sorted: ${allowedList}.`,
    });
}

// ============================================================================
// Common Response Schemas
// ============================================================================

/**
 * Success branch: status is true, no errorMessage.
 */
export const statusResponseSuccess = z.object({
  status: z.literal(true),
});

/**
 * Failure branch: status is false, errorMessage is guaranteed.
 */
export const statusResponseFailure = z.object({
  status: z.literal(false),
  errorMessage: z.string(),
});

/**
 * Basic status response as a discriminated union.
 * When status is false, errorMessage is guaranteed to be a string.
 * Use extendStatusResponse() to add fields to both branches.
 */
export const statusResponse = z.discriminatedUnion("status", [
  statusResponseSuccess,
  statusResponseFailure,
]);

/**
 * Extend the status response discriminated union with additional fields.
 * Replaces the old `statusResponse.extend({...})` pattern.
 */
export function extendStatusResponse<T extends z.ZodRawShape>(shape: T) {
  const success = statusResponseSuccess.extend(shape);
  const failure = statusResponseFailure.extend(shape).extend({ errorMessage: z.string() });

  return z.discriminatedUnion("status", [
    success as unknown as z.ZodObject<{ status: z.ZodLiteral<true> } & T>,
    failure as unknown as z.ZodObject<
      { status: z.ZodLiteral<false>; errorMessage: z.ZodString } & Omit<T, "errorMessage">
    >,
  ]);
}

/**
 * Standard API response schema with error fields.
 * Use this for responses that may include error information.
 */
export const apiResponse = z
  .object({
    status: z.boolean().meta({
      description: "Indicates whether the operation was successful",
    }),
    error: z.string().optional().meta({
      description: "Short error description",
    }),
    errorCode: z.number().optional().meta({
      description: "Numeric error code for programmatic handling",
    }),
    errorMessage: z.string().optional().meta({
      description: "Detailed error message",
    }),
    message: z.string().optional().meta({
      description: "Success or informational message",
    }),
  })
  .meta({
    description: "Standard API response with optional error fields",
  });

/**
 * Standard API response with data field.
 * Use this for responses that return data on success.
 */
export const apiResponseWithData = apiResponse
  .extend({
    data: z.unknown().optional().meta({
      description: "Response data payload",
    }),
  })
  .meta({
    description: "Standard API response with data field",
  });

/**
 * Minimal service response shape shared by many backend service methods.
 * Mirrors the former `BaseResponse` interface (helpers/scheduleInterface.ts).
 */
export const baseResponse = z
  .object({
    status: z.boolean(),
    message: z.string().optional(),
    errorMessage: z.string().optional(),
    errorCode: z.union([z.number(), z.string()]).optional(),
  })
  .meta({
    description: "Base service response with optional message/error fields",
  });
export type BaseResponse = z.infer<typeof baseResponse>;

// ============================================================================
// Grid Request Value Schema
// ============================================================================

/**
 * Common grid request value schema.
 * Used for paginated, sortable, filterable grid requests.
 */
export const gridRequestValue = z
  .object({
    where: filterWhereClause,
    sorted: sortConfigArray,
    skip: z.number().nonnegative().optional().meta({
      description: "Number of records to skip for pagination",
    }),
    take: z.number().nonnegative().optional().meta({
      description: "Number of records to take for pagination",
    }),
    requiresCounts: z.boolean().optional().meta({
      description: "Whether to return total count for pagination",
    }),
  })
  .meta({
    description: "Common grid request parameters for pagination, sorting, and filtering",
  });

// ============================================================================
// Where Clause / Predicate Schema
// ============================================================================

/**
 * Single filter predicate for grid queries.
 */
const predicateSchema = z.object({
  field: z.string(),
  operator: z.string(),
  value: z.unknown(),
  isComplex: z.boolean().optional(),
  ignoreCase: z.boolean().optional(),
  ignoreAccent: z.boolean().optional(),
});

/**
 * Where clause schema supporting both simple predicates and compound conditions.
 * Used across multiple domains (estimates, currency, etc.) for grid filtering.
 */
export const whereClauseSchema = z.array(z.union([
  predicateSchema,
  z.object({
    condition: z.string(),
    isComplex: z.boolean().optional(),
    ignoreCase: z.boolean().optional(),
    ignoreAccent: z.boolean().optional(),
    predicates: z.array(predicateSchema),
  }),
])).optional();

// ============================================================================
// Type Exports
// ============================================================================

export type ObjectIdString = z.infer<typeof objectIdString>;
export type EmailString = z.infer<typeof emailString>;
export type TempUploadKey = z.infer<typeof tempUploadKey>;
export type FilterWhereClause = z.infer<typeof filterWhereClause>;
export type SortConfig = z.infer<typeof sortConfig>;
export type SortConfigArray = z.infer<typeof sortConfigArray>;
export type StatusResponse = z.infer<typeof statusResponse>;
export type ApiResponse = z.infer<typeof apiResponse>;
export type ApiResponseWithData = z.infer<typeof apiResponseWithData>;
export type GridRequestValue = z.infer<typeof gridRequestValue>;
export type WhereClause = z.infer<typeof whereClauseSchema>;
